import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import apiManagerApi from './api';
import type { CatalogConnector } from './ConnectorCatalog';

export type ConnectorMeta = {
  provider_key?: string;
  connector?: string;
  sync_endpoint_id?: string;
  sync_every_seconds?: number;
  callback_path?: string;
};

type Pipeline = {
  pipeline_id: string;
  endpoint_id: string;
  app_id?: string | null;
  enabled?: boolean;
  last_run_at?: string | null;
  last_success_at?: string | null;
  last_error?: string | null;
  schedule?: { every_seconds?: number };
};

type Props = {
  apiId: string;
  meta: ConnectorMeta;
  pipelines: Pipeline[];
  hasCredentials: boolean;
  onChanged: () => Promise<void> | void;
};

function when(value?: string | null) {
  if (!value) return 'never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

/** Scheduled sync controls + vendor setup steps for a catalog connector API. */
export default function ConnectorSyncCard({ apiId, meta, pipelines, hasCredentials, onChanged }: Props) {
  const [busy, setBusy] = useState<'schedule' | 'run' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogConnector | null>(null);

  // Tenant-wide sync only; per-app claim pipelines (app_id set) are managed from each app.
  const pipeline = pipelines.find((p) => p.endpoint_id === meta.sync_endpoint_id && !p.app_id) || null;
  const enabled = Boolean(pipeline?.enabled);
  const callbackUrl = meta.callback_path ? `${window.location.origin}${meta.callback_path}` : null;

  useEffect(() => {
    let cancelled = false;
    apiManagerApi.listConnectorCatalog()
      .then((resp) => {
        if (cancelled) return;
        const list: CatalogConnector[] = resp.data?.connectors || [];
        setCatalog(list.find((c) => c.provider_key === meta.provider_key) || null);
      })
      .catch(() => { /* setup steps are a convenience; controls still work */ });
    return () => { cancelled = true; };
  }, [meta.provider_key]);

  const toggle = async () => {
    try {
      setBusy('schedule');
      setError(null);
      setMessage(null);
      const resp = await apiManagerApi.setConnectorSchedule(apiId, { enabled: !enabled });
      setMessage(resp.data?.enabled ? 'Scheduled sync is on. The first run starts within a few seconds.' : 'Scheduled sync paused.');
      await onChanged();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update schedule');
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    if (!pipeline) return;
    try {
      setBusy('run');
      setError(null);
      setMessage(null);
      await apiManagerApi.runPipeline(pipeline.pipeline_id, { run_kind: 'instance_ingest' });
      setMessage('Sync started. History updates when it finishes.');
      window.setTimeout(() => { void onChanged(); }, 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to start sync');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Connector sync</Typography>
        <Chip size="small" color={enabled ? 'success' : 'default'} label={enabled ? 'Scheduled' : 'Off'} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Endpoint <code>{meta.sync_endpoint_id}</code>
        {pipeline?.schedule?.every_seconds || meta.sync_every_seconds
          ? ` every ${Math.round((pipeline?.schedule?.every_seconds || meta.sync_every_seconds || 0) / 60)} min`
          : ''}
        {pipeline ? ` · last run ${when(pipeline.last_run_at)} · last success ${when(pipeline.last_success_at)}` : ''}
      </Typography>
      {pipeline?.last_error && <Alert severity="warning" sx={{ mt: 1 }}>{pipeline.last_error}</Alert>}
      {!hasCredentials && (
        <Alert severity="info" sx={{ mt: 1 }}>Add the API key in Credentials below before switching sync on.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setMessage(null)}>{message}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          size="small"
          variant={enabled ? 'outlined' : 'contained'}
          disabled={Boolean(busy) || (!enabled && !hasCredentials)}
          onClick={toggle}
          startIcon={busy === 'schedule' ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {enabled ? 'Pause scheduled sync' : 'Enable scheduled sync'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={Boolean(busy) || !pipeline || !hasCredentials}
          onClick={runNow}
          startIcon={busy === 'run' ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
        >
          Sync now
        </Button>
      </Stack>

      {callbackUrl && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, wordBreak: 'break-all' }}>
            <strong>Vendor callback URL</strong> (optional, triggers a sync when new data lands): <code>{callbackUrl}</code>
          </Typography>
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(callbackUrl)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {catalog?.auth_setup_instructions?.length ? (
        <Box component="ol" sx={{ mt: 2, mb: 0, pl: 2.5 }}>
          {catalog.auth_setup_instructions.map((step) => (
            <Typography component="li" variant="caption" color="text.secondary" key={step}>{step}</Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
